import {
  IPayableSchema,
  IPayrollSettings,
  ScheduleStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  IScheduleDate,
  IScheduleDateUpdate
} from "@wingspanhq/payments/dist/interfaces/invoice";
import { wsMoment } from "@wingspanhq/utils/dist/date/wsMoment";

const UTC_PAYROLL_CUTOFF_HOUR = 17;
const UTC_PAYROLL_CUTOFF_MINUTES = 30;

export const getExactCutOffDate = (cutOffDate: Date) => {
  return new Date(
    Date.UTC(
      cutOffDate.getUTCFullYear(),
      cutOffDate.getUTCMonth(),
      cutOffDate.getUTCDate(),
      UTC_PAYROLL_CUTOFF_HOUR,
      UTC_PAYROLL_CUTOFF_MINUTES
    )
  );
};

/**
 * Identifies a payroll run by the pair of dates that define it. Used to locate
 * a run inside `scheduleDates` without relying on an array index captured
 * earlier — see `buildScheduleDatesUpdate`.
 */
const getScheduleDateKey = (scheduleDate: IScheduleDate) =>
  [scheduleDate.date, scheduleDate.cutOffDate]
    .map(value => (value ? new Date(value).getTime() : "none"))
    .join("|");

/**
 * Builds the `scheduleDates` payload that changes a single upcoming payroll run
 * (PLA-3355).
 *
 * `PATCH /payments/payroll-settings/:memberId` REPLACES the whole array with
 * exactly what it receives — it does not merge per index. The payroll schedule
 * screen used to send `{}` for every untouched run, which stripped their
 * `date`, `status` and (critically) `cutOffDate`. Since the screen only renders
 * runs that still have a future `cutOffDate`, one Edit/Skip/Renew silently
 * destroyed the entire "Upcoming payroll runs" list server-side.
 *
 * So every entry has to be echoed back whole. `IScheduleDateUpdate` does not
 * declare `cutOffDate`, but the API does persist it and drops it when absent —
 * hence the spread of the existing entry rather than a field-by-field copy.
 *
 * The run to change is located by its own dates rather than by a positional
 * index. The payload is positional, but an index resolved when a row rendered
 * (or when a modal opened) can point at a different run by the time the write
 * happens if `scheduleDates` changed underneath — which would silently move
 * someone else's payroll date. Returns `undefined` when the run is no longer
 * present, so callers can abort instead of writing to the wrong position.
 */
export const buildScheduleDatesUpdate = ({
  scheduleDates,
  scheduleDate,
  update
}: {
  scheduleDates: IPayrollSettings["scheduleDates"];
  scheduleDate: IScheduleDate;
  update: IScheduleDateUpdate;
}): IScheduleDateUpdate[] | undefined => {
  const targetKey = getScheduleDateKey(scheduleDate);

  const index = (scheduleDates || []).findIndex(
    item => getScheduleDateKey(item) === targetKey
  );

  if (index === -1) {
    return undefined;
  }

  return (scheduleDates || []).map((item, itemIndex) =>
    itemIndex === index ? { ...item, ...update } : { ...item }
  );
};

export const getNextPayroll = (payrollSettings: IPayrollSettings) =>
  payrollSettings.scheduleDates?.find(scheduleDate =>
    (scheduleDate.status === ScheduleStatus.Pending ||
      scheduleDate.status === ScheduleStatus.Modified) &&
    scheduleDate?.cutOffDate
      ? getExactCutOffDate(scheduleDate.cutOffDate) > new Date()
      : false
  );

export const getNextPayrollCutOffDate = (payrollSettings: IPayrollSettings) =>
  getNextPayroll(payrollSettings)?.cutOffDate;

/**
 * Finds the schedule whose run is currently in flight, falling back to the
 * next upcoming Pending/Modified one. A schedule is "currently processing"
 * when its pay date is today or in the future AND its run has already started.
 * A run has started when either:
 *   - the backend flipped it to Completed (it does so the moment the run
 *     begins, well before individual payables actually settle), or
 *   - it is still Pending/Modified but its modeled cutoff has passed (the
 *     brief pre-flip window).
 * Skipped runs are excluded — they were opted out, not processing (PLA-2870).
 *
 * Keying "started" off the Completed status — not only the modeled cutoff —
 * matters: getExactCutOffDate anchors the cutoff at a fixed UTC hour, so in
 * summer (EDT) it resolves ~1h later than the backend's real processing start.
 * A schedule the backend has already marked Completed is in flight even if
 * that modeled cutoff has not yet elapsed, so relying on the cutoff alone left
 * an early-afternoon gap where payables still rolled to the next pay period.
 *
 * The pay-date filter also drops stuck-Pending entries from old off-cycle
 * runs (their date is in the past), which is what previously required a
 * 5-business-day cutoff guard (PLA-2734, PLA-2741).
 */
export const getCurrentOrNextPayroll = (payrollSettings: IPayrollSettings) => {
  const now = new Date();
  const startOfToday = wsMoment.utc(now).startOf("day").toDate();

  const next = payrollSettings.scheduleDates?.find(
    s =>
      (s.status === ScheduleStatus.Pending ||
        s.status === ScheduleStatus.Modified) &&
      s.cutOffDate &&
      getExactCutOffDate(s.cutOffDate) > now
  );

  // Require an upcoming schedule before treating a past-cutoff entry as
  // "currently processing" — if the whole array is past-cutoff, the data is
  // stuck and we should return nothing rather than a stale run.
  const currentlyProcessing = next
    ? payrollSettings.scheduleDates
        ?.filter(
          s =>
            s.status !== ScheduleStatus.Skipped &&
            s.date &&
            new Date(s.date) >= startOfToday &&
            // Run has started: backend already flipped to Completed, or the
            // modeled cutoff has passed while it is still Pending/Modified.
            (s.status === ScheduleStatus.Completed ||
              (!!s.cutOffDate && getExactCutOffDate(s.cutOffDate) <= now))
        )
        .pop()
    : undefined;

  return currentlyProcessing ?? next;
};

/**
 * Finds the payroll schedule that will include a given payable.
 *
 * When enableProcessDaysBeforeDue is OFF, the backend applies no date filter —
 * all approved invoices go into the next run. So we return getCurrentOrNextPayroll.
 *
 * When ON, mirrors the backend's bufferedCheckDate logic:
 *   bufferedCheckDate = scheduleDate.date + processDaysBeforeDue + 1 business day
 * A payable belongs to the first pending schedule whose bufferedCheckDate
 * exceeds the payable's effective due date (client.payDate || dueDate).
 */
export const getMatchingPayroll = (
  payrollSettings: IPayrollSettings,
  payable: IPayableSchema
) => {
  if (!payrollSettings.enableProcessDaysBeforeDue) {
    return getCurrentOrNextPayroll(payrollSettings);
  }

  const effectiveDueDate = payable.client.payDate || payable.dueDate;

  const processDaysBeforeDue = payrollSettings.processDaysBeforeDue ?? 0;

  return payrollSettings.scheduleDates?.find(scheduleDate => {
    if (
      scheduleDate.status !== ScheduleStatus.Pending &&
      scheduleDate.status !== ScheduleStatus.Modified
    ) {
      return false;
    }

    const bufferedCheckDate = wsMoment
      .utc(scheduleDate.date)
      .businessAdd(processDaysBeforeDue + 1)
      .toDate();

    return effectiveDueDate < bufferedCheckDate;
  });
};
